import { ref, onMounted, onUnmounted } from 'vue'

export const DEVICE_SIZES = {
  DESKTOP_BIG: 'desktop_big',
  DESKTOP: 'desktop',
  DESKTOP_SMALL: 'desktop_small',
  TABLET: 'tablet',
  MOBILE: 'mobile',
} as const

type TDevice = (typeof DEVICE_SIZES)[keyof typeof DEVICE_SIZES]

export function useScreenSize() {
  const activeDevice = ref<TDevice>(DEVICE_SIZES.DESKTOP)

  // Initialize media query variables
  const mediaQuerySmall = ref<MediaQueryList | null>(null)
  const mediaQueryMedium = ref<MediaQueryList | null>(null)
  const mediaQueryNormal = ref<MediaQueryList | null>(null)
  const mediaQueryLarge = ref<MediaQueryList | null>(null)
  const mediaQueryExtraLarge = ref<MediaQueryList | null>(null)

  // Check if window is defined (client-side only)
  const isClient = typeof window !== 'undefined'

  // Initialize media queries only on client-side
  if (isClient) {
    mediaQuerySmall.value = window.matchMedia('(max-width: 768px)')
    mediaQueryMedium.value = window.matchMedia(
      '(min-width: 769px) and (max-width: 1024px)',
    )
    mediaQueryNormal.value = window.matchMedia(
      '(min-width: 1025px) and (max-width: 1280px)',
    )
    mediaQueryLarge.value = window.matchMedia(
      '(min-width: 1281px) and (max-width: 1535px)',
    )
    mediaQueryExtraLarge.value = window.matchMedia('(min-width: 1536px)')
  }

  const getBreakpoints = () => {
    if (!isClient) return

    if (mediaQueryExtraLarge.value?.matches) {
      activeDevice.value = DEVICE_SIZES.DESKTOP_BIG
    }
    if (mediaQueryLarge.value?.matches) {
      activeDevice.value = DEVICE_SIZES.DESKTOP
    }
    if (mediaQueryNormal.value?.matches) {
      activeDevice.value = DEVICE_SIZES.DESKTOP_SMALL
    }
    if (mediaQueryMedium.value?.matches) {
      activeDevice.value = DEVICE_SIZES.TABLET
    }
    if (mediaQuerySmall.value?.matches) {
      activeDevice.value = DEVICE_SIZES.MOBILE
    }
  }

  const isBigDesktop = computed(
    () => activeDevice.value === DEVICE_SIZES.DESKTOP_BIG,
  )
  const isMobile = computed(() => activeDevice.value === DEVICE_SIZES.MOBILE)
  const isTablet = computed(() => activeDevice.value === DEVICE_SIZES.TABLET)
  const isNotDesktop = computed(() => isMobile.value || isTablet.value)

  onMounted(async () => {
    if (!isClient) return

    await nextTick(() => {
      getBreakpoints()
      mediaQuerySmall.value?.addEventListener('change', getBreakpoints)
      mediaQueryMedium.value?.addEventListener('change', getBreakpoints)
      mediaQueryNormal.value?.addEventListener('change', getBreakpoints)
      mediaQueryLarge.value?.addEventListener('change', getBreakpoints)
      mediaQueryExtraLarge.value?.addEventListener('change', getBreakpoints)
    })
  })

  onUnmounted(() => {
    if (!isClient) return

    mediaQuerySmall.value?.removeEventListener('change', getBreakpoints)
    mediaQueryMedium.value?.removeEventListener('change', getBreakpoints)
    mediaQueryNormal.value?.removeEventListener('change', getBreakpoints)
    mediaQueryLarge.value?.removeEventListener('change', getBreakpoints)
    mediaQueryExtraLarge.value?.removeEventListener('change', getBreakpoints)
  })

  return {
    activeDevice,
    isBigDesktop,
    isMobile,
    isTablet,
    isNotDesktop,
  }
}
